import { type ReactNode, useCallback, useMemo } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';

import { UrlParams } from '@/common/enums';
import { useLanguage } from '@/common/hooks';
import { getUrlParam } from '@/common/utils';

import {
  TEAM_INFO_MOCK_DATA,
  STATISTICS_MOCK_DATA,
  PLAYERS_TAB_MOCK_DATA,
  LINEUP_MOCK_DATA,
  TABLE_MOCK_DATA,
  GROUP_TABLE_MOCK_DATA,
} from '../../constants';
import { PrematchDataContext, type PrematchDataContextType } from '../../store';
import {
  LineupTabResponseSchema,
  PlayersTabResponseSchema,
  StatisticsTabResponseSchema,
  TableTabResponseSchema,
  TeamInfoApiDataSchema,
} from '../../schema';
import getData from '../../utils/getData';

interface PrematchDataProviderProps {
  eventId?: string;
  isFixtures?: boolean;
  children?: ReactNode;
}

export default function PrematchDataProvider(props: PrematchDataProviderProps) {
  const { eventId = getUrlParam(UrlParams.EVENT_ID) ?? '', children } = props;

  const paramIsFixtures = getUrlParam(UrlParams.FIXTURES);
  const isFixtures = props.isFixtures ?? (paramIsFixtures !== null && paramIsFixtures !== 'false');
  const isMockGroupTableVisible = getUrlParam(UrlParams.IS_GROUP_TABLE_VISIBLE) === 'true';

  const language = useLanguage();

  const teamsQuery = useQuery({
    queryKey: ['prematch', 'teams', eventId, isFixtures, language],
    queryFn: () =>
      getData({
        path: 'team-info',
        eventId,
        language,
        schema: TeamInfoApiDataSchema,
      }),
    placeholderData: keepPreviousData,
    retry: 2,
  });

  // First load: fetch in order (teams → stats → table → players → lineup).
  // After each query fetched once, keep it enabled so "refetch all" can run in parallel.
  const statsQuery = useQuery({
    enabled: !teamsQuery.isPending || teamsQuery.isFetched,
    queryKey: ['prematch', 'prematchStats', eventId, isFixtures, language],
    queryFn: () =>
      getData({
        path: 'stats-tab',
        eventId,
        language,
        schema: StatisticsTabResponseSchema,
      }),
    placeholderData: keepPreviousData,
    retry: 2,
    refetchInterval: false,
    staleTime: Infinity,
  });

  const tableQuery = useQuery({
    enabled: !statsQuery.isPending || statsQuery.isFetched,
    queryKey: ['prematch', 'prematchTable', eventId, isFixtures, language],
    queryFn: () =>
      getData({
        path: 'table-tab',
        eventId,
        language,
        schema: TableTabResponseSchema,
      }),
    placeholderData: keepPreviousData,
    retry: 2,
    refetchInterval: false,
    staleTime: Infinity,
  });

  const playersQuery = useQuery({
    enabled: !tableQuery.isPending || tableQuery.isFetched,
    queryKey: ['prematch', 'players', eventId, isFixtures, language],
    queryFn: () =>
      getData({
        path: 'players-tab',
        eventId,
        language,
        schema: PlayersTabResponseSchema,
      }),
    placeholderData: keepPreviousData,
    retry: 2,
    refetchInterval: false,
    staleTime: Infinity,
  });

  const lineupQuery = useQuery({
    enabled: !playersQuery.isPending || playersQuery.isFetched,
    queryKey: ['prematch', 'prematchLineup', eventId, isFixtures, language],
    queryFn: () =>
      getData({
        path: 'lineup-tab',
        eventId,
        language,
        schema: LineupTabResponseSchema,
      }),
    placeholderData: keepPreviousData,
    retry: 2,
    refetchInterval: false,
    staleTime: Infinity,
  });

  const queryClient = useQueryClient();
  const refetchAll = useCallback(() => {
    queryClient.refetchQueries({ queryKey: ['prematch'] });
  }, [queryClient]);

  const contextValue = useMemo<PrematchDataContextType>(() => {
    if (isFixtures) {
      const tableMock = isMockGroupTableVisible ? GROUP_TABLE_MOCK_DATA : TABLE_MOCK_DATA;

      return {
        teamInfo: TEAM_INFO_MOCK_DATA,
        statistics: STATISTICS_MOCK_DATA,
        table: tableMock,
        players: PLAYERS_TAB_MOCK_DATA,
        lineup: LINEUP_MOCK_DATA,
        statisticsStatus: 'success',
        tableStatus: 'error',
        playersStatus: 'success',
        lineupStatus: 'success',
        refetchAll,
      };
    }

    return {
      teamInfo: teamsQuery.data ?? null,
      statistics: statsQuery.data ?? null,
      table: tableQuery.data ?? null,
      players: playersQuery.data ?? null,
      lineup: lineupQuery.data ?? null,
      statisticsStatus: statsQuery.status,
      tableStatus: tableQuery.status,
      playersStatus: playersQuery.status,
      lineupStatus: lineupQuery.status,
      refetchAll,
    };
  }, [
    isFixtures,
    isMockGroupTableVisible,
    teamsQuery.data,
    statsQuery.data,
    tableQuery.data,
    playersQuery.data,
    lineupQuery.data,
    statsQuery.status,
    tableQuery.status,
    playersQuery.status,
    lineupQuery.status,
    refetchAll,
  ]);

  return (
    <PrematchDataContext.Provider value={contextValue}>{children}</PrematchDataContext.Provider>
  );
}
